import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Layers, Sparkles, Save, FileJson, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, fmtDate, fmtMoney, usePolling } from "@/lib/api";
import { PageHeader, Panel, EmptyState, Spinner, Alert, WorkingTerminal, Tag } from "@/components/common";
import PlanView from "@/components/plan/PlanView";

const LaunchBanner = ({ plan, live }) => {
  const planned = Object.fromEntries((plan?.campaigns || []).map((c) => [c.name, c.daily_budget]));
  return (
    <div className="cc-card mb-6 p-5" style={{ boxShadow: "inset 3px 0 0 #f59e0b" }} data-testid="plan-launch-banner">
      <div className="text-sm font-semibold text-amber-300" data-testid="plan-launch-banner-text">{live.banner || "Launched · waiting for the first Google Ads sync"}</div>
      {live.ad_schedule && <div className="mt-1 text-xs text-slate-300" data-testid="plan-live-schedule">Ad schedule: {live.ad_schedule} (SGT)</div>}
      <p className="mt-1 text-xs text-slate-400">Live budgets come from the latest Google Ads sync{live.synced_at ? ` (${fmtDate(live.synced_at)})` : ""}; monthly = daily × {live.days_per_month}. The plan below is the original pre-launch AI plan, kept for reference.</p>
      <table className="mt-3 w-full text-left text-xs" data-testid="plan-live-budgets">
        <thead className="text-slate-500"><tr>{["Campaign", "Status", "Live / day", "Live / month", "Original plan / day"].map((h) => <th key={h} className="px-2 py-1 font-medium">{h}</th>)}</tr></thead>
        <tbody>
          {live.campaigns.map((c, i) => (
            <tr key={c.name} className="border-t border-[#1e293b] text-slate-300" data-testid={`plan-live-row-${i}`}>
              <td className="px-2 py-1.5 text-slate-100">{c.name}</td>
              <td className="px-2 py-1.5"><Tag tone={c.status === "ENABLED" ? "emerald" : "amber"}>{c.status}</Tag>{c.serving && <span className="ml-2 text-slate-500">{c.serving}</span>}</td>
              <td className="px-2 py-1.5 font-mono font-bold text-emerald-300">{fmtMoney(c.daily_budget)}</td>
              <td className="px-2 py-1.5 font-mono text-slate-200">{fmtMoney(c.monthly_budget)}</td>
              <td className="px-2 py-1.5 font-mono text-[11px] text-slate-500">{fmtMoney(planned[c.name])}</td>
            </tr>
          ))}
          <tr className="border-t border-[#1e293b] font-bold text-slate-200"><td className="px-2 py-1.5">Live total</td><td />
            <td className="px-2 py-1.5 font-mono text-emerald-300" data-testid="plan-live-total-daily">{fmtMoney(live.total_daily)}</td>
            <td className="px-2 py-1.5 font-mono text-emerald-300" data-testid="plan-live-total-monthly">{fmtMoney(live.total_monthly)}</td>
            <td className="px-2 py-1.5 font-mono text-[11px] font-normal text-slate-500">{fmtMoney(plan?.total_monthly_budget)}/mo</td></tr>
        </tbody>
      </table>
      {live.other_campaigns?.length > 0 && <div className="mt-2 text-[11px] text-slate-500" data-testid="plan-other-campaigns">Other campaigns: {live.other_campaigns.map((o) => `${o.name} (${o.status.toLowerCase()})`).join(", ")}</div>}
    </div>
  );
};

const useLiveBudgets = (created) => {
  const [live, setLive] = useState(null);
  useEffect(() => { api.get("/live-budgets").then((r) => setLive(r.data)).catch(() => setLive(null)); }, []);
  return live?.banner && created && new Date(created) <= new Date(live.launched_at) ? live : null;
};

const GeneratorForm = ({ status, onCreated, disabled, profileBudget }) => {
  const [budget, setBudget] = useState("");
  const [goals, setGoals] = useState("");
  const canUseAds = Boolean(status?.connected && status?.selected_account);
  const [useAds, setUseAds] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/plans", { monthly_budget: budget ? Number(budget) : null, goals: goals || null, use_ads_data: canUseAds && useAds });
      onCreated(data);
      toast.message("Claude Sonnet 5.5 is drafting your campaign plan…");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Plan inputs" icon={Sparkles} testid="plan-generator-panel" className="mb-6">
      <form onSubmit={submit} className="grid gap-5 lg:grid-cols-6">
        <label className="lg:col-span-1"><span className="cc-eyebrow">Monthly budget (SGD)</span>
          <input data-testid="plan-budget-input" type="number" min="0" step="50" className="cc-input mt-2 font-mono" value={profileBudget ?? budget} disabled={profileBudget != null} onChange={(e) => setBudget(e.target.value)} placeholder="AI suggests" />
          {profileBudget != null && <span className="mt-1 block text-[11px] text-slate-500">From Business Profile</span>}</label>
        <label className="lg:col-span-3"><span className="cc-eyebrow">Goals / notes (optional)</span>
          <input data-testid="plan-goals-input" className="cc-input mt-2" value={goals} onChange={(e) => setGoals(e.target.value)} placeholder="e.g. More back-pain bookings near Tanjong Pagar" /></label>
        <div className="lg:col-span-1"><span className="cc-eyebrow">Use Ads data</span>
          <div className="mt-3 flex items-center gap-2">
            <Switch data-testid="plan-use-ads-switch" checked={canUseAds && useAds} disabled={!canUseAds} onCheckedChange={setUseAds} />
            <span className="text-xs text-slate-400">{canUseAds ? "Learn from account" : "Not connected"}</span>
          </div></div>
        <div className="flex items-end lg:col-span-1">
          <button data-testid="plan-generate-button" type="submit" disabled={busy || disabled} className="cc-btn cc-btn-primary w-full">
            {busy ? <Spinner className="h-4 w-4 text-[#03130c]" /> : <Sparkles className="h-4 w-4" />} Generate
          </button>
        </div>
      </form>
    </Panel>
  );
};

export default function CampaignPlan() {
  const { status } = useOutletContext();
  const [hasProfile, setHasProfile] = useState(null);
  const [profileBudget, setProfileBudget] = useState(null);
  const [domain, setDomain] = useState("");
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadList = useCallback(async () => {
    const { data } = await api.get("/plans");
    setPlans(data.plans);
    return data.plans;
  }, []);
  const loadOne = useCallback(async (id) => {
    const { data } = await api.get(`/plans/${id}`);
    setCurrent(data);
    setDraft(data.plan ? structuredClone(data.plan) : null);
    setDirty(false);
  }, []);

  useEffect(() => {
    api.get("/profile").then(({ data }) => {
      setHasProfile(Boolean(data.profile_doc?.profile));
      setProfileBudget(data.profile_doc?.profile?.monthly_budget ?? null);
      setDomain((data.profile_doc?.website || data.default_website).replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, ""));
    });
    loadList().then((list) => list[0] && loadOne(list[0].id));
  }, [loadList, loadOne]);

  const generating = current?.status === "generating";
  const live = useLiveBudgets(current?.created_at);
  const refresh = useCallback(() => current && loadOne(current.id).then(loadList), [current, loadOne, loadList]);
  usePolling(refresh, generating, 4000);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/plans/${current.id}`, { plan: draft });
      setCurrent(data);
      setDirty(false);
      toast.success(data.validation.all_valid ? "Plan saved · all ad copy within limits" : "Plan saved · some items still exceed limits");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(draft, null, 2)], { type: "application/json" });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: `ads-monster-plan-${current.id}.json` });
    a.click();
  };

  if (hasProfile === null) return <Spinner />;

  return (
    <div data-testid="plan-page">
      <PageHeader eyebrow="AI strategist · send campaigns to Approvals via the Campaign Builder" title="Campaign Plan"
        description="A complete Search build — campaigns, ad groups, match-typed keywords, negatives, responsive search ads, budgets and bidding — drafted by Claude Sonnet 5.5 and validated against Google's character limits."
        actions={draft && (
          <>
            {plans.length > 1 && (
              <select data-testid="plan-history-select" className="cc-input w-auto font-mono text-xs" value={current?.id || ""} onChange={(e) => loadOne(e.target.value)}>
                {plans.map((p) => <option key={p.id} value={p.id}>{fmtDate(p.created_at)} · {p.status}</option>)}
              </select>
            )}
            <button data-testid="plan-export-button" onClick={exportJson} className="cc-btn cc-btn-ghost"><FileJson className="h-4 w-4" /> JSON</button>
            <button data-testid="plan-save-button" onClick={save} disabled={!dirty || saving} className="cc-btn cc-btn-primary">{saving ? <Spinner className="h-4 w-4 text-[#03130c]" /> : <Save className="h-4 w-4" />} Save edits</button>
          </>
        )} />
      {!hasProfile ? (
        <EmptyState testid="plan-profile-needed" icon={Building2} title="Build the business profile first" description="The plan is grounded in facts from therehablabsg.com. Crawl the site once, then come back.">
          <Link to="/profile" data-testid="plan-go-profile-button" className="cc-btn cc-btn-primary">Go to Business Profile</Link>
        </EmptyState>
      ) : (
        <>
          <GeneratorForm status={status} profileBudget={profileBudget} disabled={generating} onCreated={(p) => { setCurrent(p); setDraft(null); loadList(); }} />
          {generating && <WorkingTerminal testid="plan-progress" title="Claude Sonnet 5.5 is building the plan (1–3 min)" lines={["Mapping services to campaign structure", "Writing keywords with match types and negatives", "Writing RSAs and validating 30/90 character limits"]} />}
          {current?.status === "error" && <Alert testid="plan-failed" title="Plan generation failed">{current.error}</Alert>}
          {!current && <EmptyState testid="plan-empty-state" icon={Layers} title="No plan yet" description="Set an optional budget and goal, then generate. Works with or without Google Ads connected." />}
          {current?.status === "ready" && draft && (
            <>
              {live && <LaunchBanner plan={draft} live={live} />}
              <div className="mb-4 font-mono text-xs text-slate-500" data-testid="plan-meta">{fmtDate(current.completed_at)} · {current.model} · {current.used_ads_data ? "learned from Google Ads data" : "business profile only"}{current.updated_at ? ` · edited ${fmtDate(current.updated_at)}` : ""}</div>
              <PlanView plan={draft} live={live} validation={current.validation} domain={domain} onChange={(p) => { setDraft(p); setDirty(true); }} />
            </>
          )}
        </>
      )}
    </div>
  );
}
